import { useState, useEffect, useCallback, useRef } from 'react';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { getSessionFromLocalStorage } from '../utils/sessionManager';
import { ChevronDown, Play, Zap, BarChart3, RotateCcw, Copy, Check, AlertCircle, Loader2, Upload, Database, Save, PlayCircle, X } from 'lucide-react';

// Embedded ITSM seed scenarios
import itsmScenariosJson from '../imports/pasted_text/itsm-scenarios.json';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Scenario {
  scenario_id: string;
  name: string;
  tags: string[];
  inputs: {
    sow?: string;
    pain_points?: string[];
    hlos?: string[];
    sales_notes?: string;
    engagement_context_override?: any;
  };
  expected?: any;
  created_at?: string;
}

interface EngineResults {
  change_order?: any;
  reporting_blueprint?: any;
  readiness?: any;
}

interface EvalScores {
  change_order: number;
  reporting: number;
  readiness: number;
}

type TabKey = 'context' | 'change_order' | 'reporting' | 'readiness';
type SingleEngine = 'change_order' | 'reporting' | 'readiness';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

const DEFAULT_SCENARIO: Scenario = {
  scenario_id: '',
  name: '',
  tags: [],
  inputs: {
    sow: '',
    pain_points: [],
    hlos: [],
    sales_notes: '',
  },
};

// ---------------------------------------------------------------------------
// API helper
// ---------------------------------------------------------------------------
type HarnessAuthDebug = {
  hasSessionToken: boolean;
  usedSessionToken: boolean;
  hasCronSecret: boolean;
  usedCronSecret: boolean;
};

function getHarnessSessionToken(): string | null {
  // Use the app's canonical/legacy session lookup so `/internal/testing`
  // works even when the user/session is stored in legacy keys (e.g. system admin).
  const { sessionId } = getSessionFromLocalStorage();
  const token = (sessionId || '').trim();
  if (!token) return null;

  // Test harness backend currently accepts `sess_*` (and for convenience we allow `imp_*` too),
  // but if backend rejects it we'll show the correct error message.
  if (!token.startsWith('sess_') && !token.startsWith('imp_')) return null;
  return token;
}

function buildHarnessAuth(cronSecret?: string): { headers: Record<string, string>; debug: HarnessAuthDebug } {
  const sessionToken = getHarnessSessionToken();
  const hasSessionToken = !!sessionToken;
  const hasCronSecret = !!cronSecret?.trim();

  // Prefer session token over cron-secret when a session exists.
  const usedSessionToken = hasSessionToken;
  const usedCronSecret = !usedSessionToken && hasCronSecret;

  const h: Record<string, string> = {
    'Content-Type': 'application/json',
    apikey: publicAnonKey,
  };

  if (usedSessionToken) {
    h.Authorization = `Bearer ${sessionToken}`;
    h['x-session-token'] = sessionToken as string;
  } else {
    h.Authorization = `Bearer ${publicAnonKey}`;
    if (usedCronSecret) h['x-cron-secret'] = cronSecret!.trim();
  }

  return {
    headers: h,
    debug: { hasSessionToken, usedSessionToken, hasCronSecret, usedCronSecret },
  };
}

async function harnessFetch(
  path: string,
  init: RequestInit,
  cronSecret?: string,
): Promise<any> {
  const { headers, debug } = buildHarnessAuth(cronSecret);

  const res = await fetch(`${BASE}${path}`, { ...init, headers });

  const rawText = await res.text();
  let data: any = null;
  if (rawText) {
    try {
      data = JSON.parse(rawText);
    } catch {
      data = { raw: rawText };
    }
  }

  if (!res.ok) {
    if (res.status === 401) {
      if (!debug.hasSessionToken && !debug.hasCronSecret) {
        throw new Error(
          'Unauthorized: no PathKit session token was found (x-session-token) and no cron secret was provided (x-cron-secret).',
        );
      }
      if (debug.usedCronSecret) {
        throw new Error('Unauthorized: backend rejected x-cron-secret.');
      }
      if (debug.usedSessionToken) {
        throw new Error(
          'Unauthorized: backend rejected x-session-token (session may be expired or not authorized for the harness).',
        );
      }
      throw new Error(data?.error || 'Unauthorized');
    }

    throw new Error(data?.error || `HTTP ${res.status}`);
  }

  return data;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function InternalTestHarness() {
  // Auth
  const [cronSecret, setCronSecret] = useState(() => localStorage.getItem('_pk_test_cron_secret') || '');

  // Scenarios
  const [savedScenarios, setSavedScenarios] = useState<Scenario[]>([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState<string>('');
  const [scenario, setScenario] = useState<Scenario>({ ...DEFAULT_SCENARIO });
  const [jsonMode, setJsonMode] = useState(false);
  const [jsonText, setJsonText] = useState('');

  // Results
  const [results, setResults] = useState<EngineResults | null>(null);
  const [engagementContext, setEngagementContext] = useState<any>(null);
  const [evalScores, setEvalScores] = useState<EvalScores | null>(null);
  const [evalNotes, setEvalNotes] = useState<string[]>([]);
  const [meta, setMeta] = useState<any>(null);

  // UI state
  const [activeTab, setActiveTab] = useState<TabKey>('context');
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [singleEngine, setSingleEngine] = useState<SingleEngine>('change_order');

  // Bulk import state
  const [bulkResult, setBulkResult] = useState<any>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Run All state
  const [runAllData, setRunAllData] = useState<any>(null);
  const [showRunAll, setShowRunAll] = useState(false);

  // Persist cron secret
  useEffect(() => {
    if (cronSecret) localStorage.setItem('_pk_test_cron_secret', cronSecret);
  }, [cronSecret]);

  // Load saved scenarios
  const loadScenarios = useCallback(async () => {
    try {
      const data = await harnessFetch('/internal/test/scenarios', { method: 'GET' }, cronSecret);
      if (data.scenarios) setSavedScenarios(data.scenarios);
    } catch (e: any) {
      console.error('[TestHarness] Failed to load scenarios:', e);
    }
  }, [cronSecret]);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  // Select saved scenario
  const handleSelectScenario = useCallback((id: string) => {
    setSelectedScenarioId(id);
    if (!id) {
      setScenario({ ...DEFAULT_SCENARIO });
      return;
    }
    const found = savedScenarios.find(s => s.scenario_id === id);
    if (found) {
      setScenario(found);
      setJsonText(JSON.stringify(found.inputs, null, 2));
    }
  }, [savedScenarios]);

  // ---------------------------------------------------------------------------
  // Actions
  // ---------------------------------------------------------------------------
  const clearError = () => setError(null);

  const runScenario = async () => {
    clearError();
    setLoading('scenario');
    try {
      const inputs = jsonMode ? JSON.parse(jsonText) : scenario.inputs;
      const body = {
        scenario_id: scenario.scenario_id || `adhoc_${Date.now()}`,
        name: scenario.name || 'Ad-hoc test',
        inputs,
      };
      const data = await harnessFetch('/internal/test/run-scenario', {
        method: 'POST',
        body: JSON.stringify(body),
      }, cronSecret);
      setResults(data.results);
      setEngagementContext(data.engagement_context);
      setMeta(data._meta);
      setEvalScores(null);
      setEvalNotes([]);
      setActiveTab('change_order');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const runSingle = async () => {
    clearError();
    setLoading('engine');
    try {
      const inputs = jsonMode ? JSON.parse(jsonText) : scenario.inputs;
      const body = { engine: singleEngine, inputs };
      const data = await harnessFetch('/internal/test/run-engine', {
        method: 'POST',
        body: JSON.stringify(body),
      }, cronSecret);
      // Merge into results
      const engineMap: Record<SingleEngine, string> = {
        change_order: 'change_order',
        reporting: 'reporting_blueprint',
        readiness: 'readiness',
      };
      setResults(prev => ({ ...prev, [engineMap[singleEngine]]: data.result }));
      setMeta(data._meta);
      const tabMap: Record<SingleEngine, TabKey> = {
        change_order: 'change_order',
        reporting: 'reporting',
        readiness: 'readiness',
      };
      setActiveTab(tabMap[singleEngine]);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const evaluate = async () => {
    if (!results) return;
    clearError();
    setLoading('evaluate');
    try {
      const body = { scenario, results };
      const data = await harnessFetch('/internal/test/evaluate', {
        method: 'POST',
        body: JSON.stringify(body),
      }, cronSecret);
      setEvalScores(data.scores);
      setEvalNotes(data.notes || []);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const copyResults = () => {
    navigator.clipboard.writeText(JSON.stringify({ results, engagementContext, evalScores, evalNotes }, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // ---------------------------------------------------------------------------
  // Bulk import helpers
  // ---------------------------------------------------------------------------
  const bulkImport = async (payload: { scenarios: any[] }) => {
    clearError();
    setBulkResult(null);
    setLoading('bulk');
    try {
      const data = await harnessFetch('/internal/test/scenarios/bulk', {
        method: 'POST',
        body: JSON.stringify(payload),
      }, cronSecret);
      setBulkResult(data);
      await loadScenarios(); // refresh dropdown
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const seedItsmScenarios = () => bulkImport(itsmScenariosJson as any);

  // ---------------------------------------------------------------------------
  // Run All — regression scorecard
  // ---------------------------------------------------------------------------
  const runAll = async (tag?: string) => {
    clearError();
    setLoading('runall');
    setRunAllData(null);
    setShowRunAll(true);
    try {
      const data = await harnessFetch('/internal/test/run-all', {
        method: 'POST',
        body: JSON.stringify(tag ? { tag } : {}),
      }, cronSecret);
      setRunAllData(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const copyRunAll = () => {
    if (runAllData) {
      navigator.clipboard.writeText(JSON.stringify(runAllData, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // ---------------------------------------------------------------------------
  // Run Saved / Run & Evaluate (server-side, no payload reconstruction)
  // ---------------------------------------------------------------------------
  const runSaved = async () => {
    if (!selectedScenarioId) { setError('Select a saved scenario first'); return; }
    clearError();
    setLoading('scenario');
    try {
      const data = await harnessFetch(
        `/internal/test/run-saved/${encodeURIComponent(selectedScenarioId)}`,
        {
        method: 'POST',
        },
        cronSecret,
      );
      setResults(data.results);
      setEngagementContext(data.engagement_context);
      setMeta({ ...data._meta, timing: data.timing });
      setEvalScores(null);
      setEvalNotes([]);
      setActiveTab('change_order');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const runAndEvaluate = async () => {
    if (!selectedScenarioId) { setError('Select a saved scenario first'); return; }
    clearError();
    setLoading('scenario');
    try {
      const data = await harnessFetch(
        `/internal/test/run-and-evaluate/${encodeURIComponent(selectedScenarioId)}`,
        {
        method: 'POST',
        },
        cronSecret,
      );
      setResults(data.results);
      setEngagementContext(data.engagement_context);
      setMeta({ ...data._meta, timing: data.timing });
      if (data.evaluation) {
        setEvalScores(data.evaluation.scores);
        setEvalNotes(data.evaluation.notes || []);
      }
      setActiveTab('change_order');
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  const handleFileImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string);
        if (parsed.scenarios) {
          bulkImport(parsed);
        } else {
          setError('JSON must contain a "scenarios" array at the top level');
        }
      } catch {
        setError('Invalid JSON file');
      }
    };
    reader.readAsText(file);
    // Reset so the same file can be re-selected
    e.target.value = '';
  };

  // ---------------------------------------------------------------------------
  // Save current scenario
  // ---------------------------------------------------------------------------
  const [saveSuccess, setSaveSuccess] = useState<string | null>(null);

  const saveCurrentScenario = async () => {
    const id = scenario.scenario_id?.trim();
    const name = scenario.name?.trim();
    if (!id) { setError('Scenario ID is required to save'); return; }
    if (!name) { setError('Name is required to save'); return; }
    clearError();
    setSaveSuccess(null);
    setLoading('save');
    try {
      const inputs = jsonMode ? JSON.parse(jsonText) : scenario.inputs;
      const body = {
        scenario_id: id,
        name,
        tags: scenario.tags || [],
        inputs,
        expected: scenario.expected || null,
      };
      const data = await harnessFetch('/internal/test/scenarios', {
        method: 'POST',
        body: JSON.stringify(body),
      }, cronSecret);
      setSaveSuccess(id);
      setTimeout(() => setSaveSuccess(null), 2000);
      await loadScenarios();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const updateField = (field: string, value: any) => {
    setScenario(prev => ({ ...prev, inputs: { ...prev.inputs, [field]: value } }));
  };

  const updateArrayField = (field: string, value: string) => {
    const arr = value.split('\n').filter(Boolean);
    updateField(field, arr);
  };

  const ScoreBadge = ({ score, label }: { score: number; label: string }) => {
    const color = score >= 4 ? 'bg-green-500/20 text-green-300 border-green-500/30'
      : score >= 2 ? 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30'
      : 'bg-red-500/20 text-red-300 border-red-500/30';
    return (
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-mono ${color}`}>
        <span className="opacity-60">{label}</span>
        <span className="font-bold">{score}/5</span>
      </div>
    );
  };

  const JsonBlock = ({ data, maxHeight = '400px' }: { data: any; maxHeight?: string }) => (
    <pre
      className="bg-zinc-950 border border-zinc-800 rounded-lg p-3 text-xs text-zinc-300 overflow-auto font-mono"
      style={{ maxHeight }}
    >{data ? JSON.stringify(data, null, 2) : 'No data'}</pre>
  );

  const tabData: { key: TabKey; label: string; resultKey?: string }[] = [
    { key: 'context', label: 'Engagement Context' },
    { key: 'change_order', label: 'Change Order', resultKey: 'change_order' },
    { key: 'reporting', label: 'Reporting Blueprint', resultKey: 'reporting_blueprint' },
    { key: 'readiness', label: 'Readiness', resultKey: 'readiness' },
  ];

  const activeResult = activeTab === 'context'
    ? engagementContext
    : results?.[tabData.find(t => t.key === activeTab)?.resultKey as keyof EngineResults];

  // Summary extraction
  const getSummary = (tab: TabKey, data: any): string[] => {
    if (!data) return ['No data available. Run a scenario first.'];
    const lines: string[] = [];
    try {
      if (tab === 'context') {
        const pg = data?.project_goals;
        if (pg) {
          lines.push(`Schema: ${pg.schema_version || 'unknown'}`);
          lines.push(`Objectives: ${pg.objectives?.length || 0}`);
          lines.push(`Pain Points: ${pg.pain_points?.length || 0}`);
          lines.push(`Confidence: ${pg.confidence_summary?.overall || 'N/A'}`);
          if (pg.confidence_summary?.warnings?.length) {
            lines.push(`Warnings: ${pg.confidence_summary.warnings.join('; ')}`);
          }
        }
      } else if (tab === 'change_order') {
        if (data._note) lines.push(`Note: ${data._note}`);
        if (data.items?.length) lines.push(`Items: ${data.items.length}`);
        if (data.summary) lines.push(`Summary: ${String(data.summary).slice(0, 200)}`);
        if (data.risk_level) lines.push(`Risk: ${data.risk_level}`);
        if (data.total_impact) lines.push(`Impact: ${data.total_impact}`);
      } else if (tab === 'reporting') {
        if (data.kpis?.length) lines.push(`KPIs: ${data.kpis.length}`);
        if (data.dashboards?.length) lines.push(`Dashboards: ${data.dashboards.length}`);
        if (data.data_sources?.length) lines.push(`Data Sources: ${data.data_sources.length}`);
        if (data.gaps?.length) lines.push(`Gaps: ${data.gaps.length}`);
      } else if (tab === 'readiness') {
        if (data.score !== undefined) lines.push(`Score: ${data.score}`);
        if (data.readiness_score !== undefined) lines.push(`Readiness Score: ${data.readiness_score}`);
        if (data.overall_score !== undefined) lines.push(`Overall: ${data.overall_score}`);
        if (data.recommendations?.length) lines.push(`Recommendations: ${data.recommendations.length}`);
        if (data.missing?.length) lines.push(`Missing Inputs: ${data.missing.length}`);
      }
    } catch { /* ignore */ }
    return lines.length ? lines : ['Output received. Inspect JSON below for details.'];
  };

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 flex flex-col" style={{ fontFamily: "'JetBrains Mono', 'Fira Code', monospace" }}>
      {/* Header */}
      <header className="border-b border-zinc-800 px-4 py-3 flex items-center justify-between shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <h1 className="text-sm font-bold tracking-wide text-zinc-200">PATHKIT TEST HARNESS</h1>
          <span className="text-xs text-zinc-600">internal</span>
          <a
            href="/internal/control-grid"
            className="ml-2 text-[11px] px-2 py-0.5 rounded border border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700 transition-colors"
            title="Open the read-only product health view"
          >
            control grid →
          </a>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="password"
            placeholder="cron secret"
            value={cronSecret}
            onChange={e => setCronSecret(e.target.value)}
            className="bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs w-40 text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
          />
          {results && (
            <button onClick={copyResults} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors">
              {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>
      </header>

      {/* Error banner */}
      {error && (
        <div className="bg-red-950/50 border-b border-red-800/50 px-4 py-2 flex items-center gap-2 text-xs text-red-300">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          <span className="flex-1">{error}</span>
          <button onClick={clearError} className="text-red-500 hover:text-red-300">&times;</button>
        </div>
      )}

      <div className="flex flex-1 overflow-hidden">
        {/* ===================== LEFT PANEL ===================== */}
        <aside className="w-80 border-r border-zinc-800 flex flex-col shrink-0 overflow-y-auto">
          {/* Saved scenario selector */}
          <div className="p-3 border-b border-zinc-800/50">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 mb-1 block">Load Saved Scenario</label>
            <div className="relative">
              <select
                value={selectedScenarioId}
                onChange={e => handleSelectScenario(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1.5 text-xs text-zinc-300 appearance-none pr-6 focus:outline-none focus:border-zinc-500"
              >
                <option value="">-- new / blank --</option>
                {savedScenarios.map(s => (
                  <option key={s.scenario_id} value={s.scenario_id}>
                    {s.name || s.scenario_id}
                    {s.tags?.length ? ` [${s.tags.join(', ')}]` : ''}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-3 h-3 absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            </div>
            <button
              onClick={loadScenarios}
              className="mt-1 text-[10px] text-zinc-500 hover:text-zinc-300 transition-colors"
            >
              refresh list
            </button>
          </div>

          {/* Bulk seed / import */}
          <div className="p-3 border-b border-zinc-800/50 space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-zinc-500 block">Bulk Import</label>
            <div className="flex items-center gap-1.5">
              <button
                onClick={seedItsmScenarios}
                disabled={!!loading}
                className="flex-1 inline-flex items-center justify-center gap-1.5 bg-purple-600/20 hover:bg-purple-600/30 border border-purple-500/30 text-purple-300 text-[10px] font-medium px-2 py-1.5 rounded transition-colors disabled:opacity-40"
              >
                {loading === 'bulk' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Database className="w-3 h-3" />}
                Seed ITSM ({(itsmScenariosJson as any).scenarios?.length || 0})
              </button>
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={!!loading}
                className="inline-flex items-center justify-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 text-zinc-300 text-[10px] font-medium px-2 py-1.5 rounded transition-colors disabled:opacity-40"
              >
                <Upload className="w-3 h-3" />
                File
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept=".json"
                onChange={handleFileImport}
                className="hidden"
              />
            </div>
            {bulkResult && (
              <div className="bg-zinc-900 border border-zinc-800 rounded p-2 text-[10px] space-y-0.5">
                <p className="text-green-400">{bulkResult.created} created</p>
                {bulkResult.updated > 0 && <p className="text-blue-400">{bulkResult.updated} updated</p>}
                {bulkResult.failed > 0 && <p className="text-red-400">{bulkResult.failed} failed</p>}
                <p className="text-zinc-500">{bulkResult.total} total</p>
                <button onClick={() => setBulkResult(null)} className="text-zinc-600 hover:text-zinc-400 underline">dismiss</button>
              </div>
            )}
          </div>

          {/* Input mode toggle */}
          <div className="px-3 pt-3 flex items-center gap-2">
            <button
              onClick={() => { setJsonMode(false); }}
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${!jsonMode ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}
            >Form</button>
            <button
              onClick={() => { setJsonMode(true); setJsonText(JSON.stringify(scenario.inputs, null, 2)); }}
              className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded ${jsonMode ? 'bg-zinc-700 text-zinc-200' : 'text-zinc-500 hover:text-zinc-300'}`}
            >JSON</button>
          </div>

          {/* Form / JSON input */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3">
            {jsonMode ? (
              <textarea
                value={jsonText}
                onChange={e => setJsonText(e.target.value)}
                spellCheck={false}
                className="w-full h-full min-h-[300px] bg-zinc-900 border border-zinc-700 rounded p-2 text-xs text-zinc-300 font-mono resize-none focus:outline-none focus:border-zinc-500"
              />
            ) : (
              <>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Scenario ID</label>
                  <input
                    value={scenario.scenario_id}
                    onChange={e => setScenario(prev => ({ ...prev, scenario_id: e.target.value }))}
                    placeholder="e.g. scope-creep-01"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Name</label>
                  <input
                    value={scenario.name}
                    onChange={e => setScenario(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Human-readable name"
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">SOW</label>
                  <textarea
                    value={scenario.inputs.sow || ''}
                    onChange={e => updateField('sow', e.target.value)}
                    placeholder="Paste SOW text..."
                    rows={4}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 resize-none focus:outline-none focus:border-zinc-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">HLOs (one per line)</label>
                  <textarea
                    value={(scenario.inputs.hlos || []).join('\n')}
                    onChange={e => updateArrayField('hlos', e.target.value)}
                    placeholder="Reduce MTTR by 40%&#10;Automate ticket routing"
                    rows={3}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 resize-none focus:outline-none focus:border-zinc-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Pain Points (one per line)</label>
                  <textarea
                    value={(scenario.inputs.pain_points || []).join('\n')}
                    onChange={e => updateArrayField('pain_points', e.target.value)}
                    placeholder="No SLA tracking&#10;Manual ticket routing"
                    rows={3}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 resize-none focus:outline-none focus:border-zinc-500"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Sales Notes</label>
                  <textarea
                    value={scenario.inputs.sales_notes || ''}
                    onChange={e => updateField('sales_notes', e.target.value)}
                    placeholder="Optional context from sales..."
                    rows={2}
                    className="w-full bg-zinc-900 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-300 placeholder:text-zinc-600 resize-none focus:outline-none focus:border-zinc-500"
                  />
                </div>
              </>
            )}
          </div>

          {/* Save button */}
          <div className="px-3 pb-3">
            <button
              onClick={saveCurrentScenario}
              disabled={!!loading}
              className="inline-flex items-center gap-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 text-xs font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-40"
            >
              {loading === 'save' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
              Save Scenario
            </button>
            {saveSuccess && (
              <span className="text-[10px] text-emerald-400 ml-2">Saved as {saveSuccess}</span>
            )}
          </div>
        </aside>

        {/* ===================== MAIN PANEL ===================== */}
        <main className="flex-1 flex flex-col overflow-hidden">
          {/* Top controls */}
          <div className="border-b border-zinc-800 px-4 py-2 flex items-center gap-2 flex-wrap">
            <button
              onClick={runScenario}
              disabled={!!loading}
              className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white text-xs font-medium px-3 py-1.5 rounded transition-colors"
            >
              {loading === 'scenario' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
              Run Scenario
            </button>

            <div className="flex items-center gap-1 border-l border-zinc-700 pl-2 ml-1">
              <select
                value={singleEngine}
                onChange={e => setSingleEngine(e.target.value as SingleEngine)}
                className="bg-zinc-900 border border-zinc-700 rounded px-1.5 py-1 text-xs text-zinc-300 focus:outline-none focus:border-zinc-500"
              >
                <option value="change_order">Change Order</option>
                <option value="reporting">Reporting</option>
                <option value="readiness">Readiness</option>
              </select>
              <button
                onClick={runSingle}
                disabled={!!loading}
                className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-200 text-xs font-medium px-3 py-1.5 rounded transition-colors"
              >
                {loading === 'engine' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                Run Engine
              </button>
            </div>

            <button
              onClick={evaluate}
              disabled={!results || !!loading}
              className="inline-flex items-center gap-1.5 bg-zinc-800 hover:bg-zinc-700 disabled:bg-zinc-800 disabled:text-zinc-600 text-zinc-200 text-xs font-medium px-3 py-1.5 rounded border-l border-zinc-700 ml-1 transition-colors"
            >
              {loading === 'evaluate' ? <Loader2 className="w-3 h-3 animate-spin" /> : <BarChart3 className="w-3 h-3" />}
              Evaluate
            </button>

            {/* Saved-scenario shortcuts */}
            {selectedScenarioId && (
              <div className="flex items-center gap-1 border-l border-zinc-700 pl-2 ml-1">
                <button
                  onClick={runSaved}
                  disabled={!!loading}
                  className="inline-flex items-center gap-1.5 bg-emerald-600/20 hover:bg-emerald-600/30 border border-emerald-500/30 text-emerald-300 text-xs font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-40"
                >
                  {loading === 'scenario' ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
                  Run Saved
                </button>
                <button
                  onClick={runAndEvaluate}
                  disabled={!!loading}
                  className="inline-flex items-center gap-1.5 bg-amber-600/20 hover:bg-amber-600/30 border border-amber-500/30 text-amber-300 text-xs font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-40"
                >
                  {loading === 'scenario' ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Play className="w-3 h-3" /><BarChart3 className="w-3 h-3" /></>}
                  Run + Eval
                </button>
              </div>
            )}

            <button
              onClick={() => { setResults(null); setEngagementContext(null); setEvalScores(null); setEvalNotes([]); setMeta(null); setError(null); }}
              className="inline-flex items-center gap-1.5 text-zinc-500 hover:text-zinc-300 text-xs px-2 py-1.5 rounded transition-colors ml-auto"
            >
              <RotateCcw className="w-3 h-3" />
              Clear
            </button>

            {/* Run All regression */}
            <button
              onClick={() => runAll()}
              disabled={!!loading}
              className="inline-flex items-center gap-1.5 bg-cyan-600/20 hover:bg-cyan-600/30 border border-cyan-500/30 text-cyan-300 text-xs font-medium px-3 py-1.5 rounded transition-colors disabled:opacity-40"
            >
              {loading === 'runall' ? <Loader2 className="w-3 h-3 animate-spin" /> : <PlayCircle className="w-3 h-3" />}
              Run All
            </button>
          </div>

          {/* Eval scores bar */}
          {evalScores && (
            <div className="border-b border-zinc-800 px-4 py-2 flex items-center gap-3 bg-zinc-900/50">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">Scores</span>
              <ScoreBadge label="CO" score={evalScores.change_order} />
              <ScoreBadge label="RP" score={evalScores.reporting} />
              <ScoreBadge label="RD" score={evalScores.readiness} />
              {evalNotes.length > 0 && (
                <span className="text-[10px] text-zinc-500 ml-2">
                  {evalNotes.length} note{evalNotes.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}

          {/* Tabs */}
          <div className="border-b border-zinc-800 flex">
            {tabData.map(t => (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`px-4 py-2 text-xs transition-colors border-b-2 ${
                  activeTab === t.key
                    ? 'border-blue-500 text-blue-400'
                    : 'border-transparent text-zinc-500 hover:text-zinc-300'
                }`}
              >
                {t.label}
                {t.resultKey && results?.[t.resultKey as keyof EngineResults] && (
                  <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {!results && !engagementContext ? (
              <div className="flex items-center justify-center h-full text-zinc-600 text-sm">
                <div className="text-center space-y-2">
                  <Play className="w-8 h-8 mx-auto opacity-30" />
                  <p>Configure inputs and run a scenario to see results.</p>
                </div>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                  <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Summary</h3>
                  <div className="space-y-1">
                    {getSummary(activeTab, activeResult).map((line, i) => (
                      <p key={i} className="text-xs text-zinc-300">{line}</p>
                    ))}
                  </div>
                </div>

                {/* Eval notes for this tab */}
                {evalNotes.length > 0 && (
                  <div className="bg-zinc-900/50 border border-zinc-800 rounded-lg p-3">
                    <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Evaluation Notes</h3>
                    <div className="space-y-0.5">
                      {evalNotes
                        .filter(n => {
                          if (activeTab === 'context') return true;
                          const prefix = activeTab === 'reporting' ? 'reporting:' : activeTab + ':';
                          return n.startsWith(prefix);
                        })
                        .map((note, i) => (
                          <p key={i} className="text-xs text-yellow-400/80 font-mono">{note}</p>
                        ))}
                    </div>
                  </div>
                )}

                {/* JSON */}
                <div>
                  <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Raw Output</h3>
                  <JsonBlock data={activeResult} maxHeight="500px" />
                </div>

                {/* Meta */}
                {meta && (
                  <div>
                    <h3 className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Meta</h3>
                    <JsonBlock data={meta} maxHeight="120px" />
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {/* ===================== RUN ALL SCORECARD OVERLAY ===================== */}
      {showRunAll && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-6" onClick={() => { if (!loading) setShowRunAll(false); }}>
          <div
            className="bg-zinc-900 border border-zinc-700 rounded-xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-800">
              <div className="flex items-center gap-3">
                <PlayCircle className="w-4 h-4 text-cyan-400" />
                <h2 className="text-sm font-bold text-zinc-200 tracking-wide">REGRESSION SCORECARD</h2>
                {runAllData?.tag_filter && (
                  <span className="text-[10px] bg-zinc-800 border border-zinc-700 rounded px-1.5 py-0.5 text-zinc-400">
                    tag: {runAllData.tag_filter}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {runAllData && (
                  <button onClick={copyRunAll} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors">
                    {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Copy className="w-3.5 h-3.5" />}
                  </button>
                )}
                <button onClick={() => setShowRunAll(false)} className="p-1.5 rounded hover:bg-zinc-800 text-zinc-400 hover:text-zinc-200 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Loading state */}
            {loading === 'runall' && (
              <div className="flex-1 flex items-center justify-center py-20">
                <div className="text-center space-y-3">
                  <Loader2 className="w-8 h-8 animate-spin text-cyan-400 mx-auto" />
                  <p className="text-xs text-zinc-500">Running all saved scenarios...</p>
                  <p className="text-[10px] text-zinc-600">This may take a while if Change Order uses AI</p>
                </div>
              </div>
            )}

            {/* Results */}
            {runAllData && (
              <>
                {/* Summary bar */}
                <div className="px-5 py-3 border-b border-zinc-800 flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Total</span>
                    <span className="text-sm font-bold text-zinc-200">{runAllData.summary.total}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-mono bg-green-500/20 text-green-300 border-green-500/30">
                      <span className="font-bold">{runAllData.summary.passed}</span> pass
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-mono bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                      <span className="font-bold">{runAllData.summary.warned}</span> warn
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-mono bg-red-500/20 text-red-300 border-red-500/30">
                      <span className="font-bold">{runAllData.summary.failed}</span> fail
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500">Avg</span>
                    <span className={`text-sm font-bold ${
                      runAllData.summary.avg_score >= 4 ? 'text-green-400' :
                      runAllData.summary.avg_score >= 2 ? 'text-yellow-400' : 'text-red-400'
                    }`}>{runAllData.summary.avg_score}/5</span>
                  </div>
                  <span className="text-[10px] text-zinc-600 ml-auto">{runAllData.timing.total_ms}ms</span>
                </div>

                {/* Scenario table */}
                <div className="flex-1 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-zinc-900 border-b border-zinc-800">
                      <tr className="text-[10px] uppercase tracking-widest text-zinc-500">
                        <th className="text-left px-4 py-2 font-medium">Status</th>
                        <th className="text-left px-4 py-2 font-medium">Scenario</th>
                        <th className="text-center px-2 py-2 font-medium">CO</th>
                        <th className="text-center px-2 py-2 font-medium">RP</th>
                        <th className="text-center px-2 py-2 font-medium">RD</th>
                        <th className="text-center px-2 py-2 font-medium">Avg</th>
                        <th className="text-right px-4 py-2 font-medium">Time</th>
                        <th className="text-center px-2 py-2 font-medium">Notes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {runAllData.results.map((r: any) => {
                        const statusColor = r.status === 'pass' ? 'text-green-400' :
                          r.status === 'warn' ? 'text-yellow-400' :
                          r.status === 'error' ? 'text-orange-400' : 'text-red-400';
                        const statusBg = r.status === 'pass' ? 'bg-green-500/10' :
                          r.status === 'warn' ? 'bg-yellow-500/10' :
                          r.status === 'error' ? 'bg-orange-500/10' : 'bg-red-500/10';
                        const scoreCell = (val: number | null) => {
                          if (val === null || val === undefined) return <span className="text-zinc-600">-</span>;
                          const c = val >= 4 ? 'text-green-400' : val >= 2 ? 'text-yellow-400' : 'text-red-400';
                          return <span className={`font-bold ${c}`}>{val}</span>;
                        };
                        return (
                          <tr key={r.scenario_id} className={`border-b border-zinc-800/50 hover:bg-zinc-800/30 ${statusBg}`}>
                            <td className={`px-4 py-2 font-bold uppercase text-[10px] ${statusColor}`}>
                              {r.status}
                            </td>
                            <td className="px-4 py-2">
                              <div className="text-zinc-200 font-medium">{r.name}</div>
                              <div className="text-[10px] text-zinc-500">{r.scenario_id}</div>
                            </td>
                            <td className="text-center px-2 py-2 font-mono">{scoreCell(r.scores?.change_order ?? null)}</td>
                            <td className="text-center px-2 py-2 font-mono">{scoreCell(r.scores?.reporting ?? null)}</td>
                            <td className="text-center px-2 py-2 font-mono">{scoreCell(r.scores?.readiness ?? null)}</td>
                            <td className="text-center px-2 py-2 font-mono">{scoreCell(r.scores?.avg ?? null)}</td>
                            <td className="text-right px-4 py-2 text-zinc-500">{r.timing_ms}ms</td>
                            <td className="text-center px-2 py-2">
                              {r.notes?.length > 0 ? (
                                <span className="text-yellow-500 cursor-help" title={r.notes.join('\n')}>
                                  {r.notes.length}
                                </span>
                              ) : r.error ? (
                                <span className="text-red-400 cursor-help" title={r.error}>err</span>
                              ) : (
                                <span className="text-zinc-600">0</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}